/**
 * AdminSales.jsx — Admin widget: filterable sales report + member summary.
 *
 * Modernized with ThemedTable:
 *   - Drag-to-reorder columns
 *   - Click-to-sort column headers with directional glyphs
 *   - Instant search filter & column visibility picker
 *   - Interactive UserCell & GuildCell chips
 */
import { Show, For, createSignal, createMemo } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { gold, qty } from '../lib/format.js';
import EMOJI from '../lib/emoji.js';
import ThemedTable, { GoldCell, UserCell, GuildCell, RelTimeCell } from '../components/ThemedTable/index.js';

const GUILDS = [
  { value: '',        label: 'All guilds' },
  { value: 'post',    label: 'Trading Post' },
  { value: 'caravan', label: 'Trading Caravan' },
  { value: 'dealers', label: 'Dealers' },
];

const WEEK_OPTS = [
  { value: 0,   label: 'This week' },
  { value: 1,   label: 'Last week' },
  { value: 2,   label: '2 weeks ago' },
  { value: 3,   label: '3 weeks ago' },
  { value: 4,   label: '4 weeks ago' },
  { value: 8,   label: '8 weeks ago' },
  { value: 12,  label: '12 weeks ago' },
  { value: -1,  label: 'All time' },
];

function weekLabel(off) {
  if (off === -1) return 'All time';
  if (off === 0)  return 'This week';
  if (off === 1)  return 'Last week';
  return `${off} weeks ago`;
}

export default function AdminSales() {
  const [tab,       setTab]       = createSignal('sales');
  const [weeksAgo,  setWeeksAgo]  = createSignal(0);
  const [guild,     setGuild]     = createSignal('');
  const [member,    setMember]    = createSignal('');
  const [startDate, setStartDate] = createSignal('');
  const [endDate,   setEndDate]   = createSignal('');

  const [params, setParams] = createSignal({
    tab: 'sales', weeksAgo: 0, guild: '', member: '', startDate: '', endDate: '',
  });

  const { data, loading, error, refetch } = useWidgetData('admin-sales', params);

  function applyFilters() {
    setParams({
      tab:       tab(),
      weeksAgo:  weeksAgo(),
      guild:     guild(),
      member:    member(),
      startDate: startDate(),
      endDate:   endDate(),
    });
  }

  function switchTab(t) {
    setTab(t);
    setParams(p => ({ ...p, tab: t }));
  }

  const sellers = createMemo(() => data()?.sellers || []);
  const members = createMemo(() => data()?.members || []);
  const period  = createMemo(() => weekLabel(data()?.weekOffset ?? 0));

  // ── Column Definitions for Sales Tab ──
  const salesColumns = [
    {
      key: 'sellerName',
      label: 'Seller',
      sortable: true,
      type: 'string',
      canHide: false,
      render: (_val, row) => <UserCell name={row.sellerName} />,
    },
    {
      key: 'guildName',
      label: 'Guild',
      sortable: true,
      type: 'string',
      render: (_val, row) => <GuildCell name={row.guildName} />,
    },
    {
      key: 'profit',
      label: 'Revenue',
      sortable: true,
      type: 'currency',
      align: 'right',
      render: (val) => <GoldCell value={val} />,
    },
    {
      key: 'saleCount',
      label: 'Sales',
      sortable: true,
      type: 'number',
      align: 'right',
      render: (val) => <span class="v2-tt-mono">{qty(val)}</span>,
    },
    {
      key: 'totalItems',
      label: 'Items',
      sortable: true,
      type: 'number',
      align: 'right',
      render: (val) => <span class="v2-tt-mono">{qty(val)}</span>,
    },
    {
      key: 'lastSaleTs',
      label: 'Last Sale',
      sortable: true,
      type: 'time',
      align: 'right',
      render: (val) => <RelTimeCell ts={val} />,
    },
  ];

  // ── Column Definitions for Members Tab ──
  const membersColumns = [
    {
      key: 'sellerName',
      label: 'Member',
      sortable: true,
      type: 'string',
      canHide: false,
      render: (_val, row) => <UserCell name={row.sellerName} />,
    },
    {
      key: 'totalProfit',
      label: 'Revenue',
      sortable: true,
      type: 'currency',
      align: 'right',
      render: (val) => <GoldCell value={val} />,
    },
    {
      key: 'totalSales',
      label: 'Sales',
      sortable: true,
      type: 'number',
      align: 'right',
      render: (val) => <span class="v2-tt-mono">{qty(val)}</span>,
    },
    {
      key: 'totalItems',
      label: 'Items',
      sortable: true,
      type: 'number',
      align: 'right',
      render: (val) => <span class="v2-tt-mono">{qty(val)}</span>,
    },
    {
      key: 'guilds',
      label: 'Active Guilds',
      sortable: true,
      type: 'string',
      getValue: (row) => Object.keys(row.guilds || {}).join(', '),
      render: (_val, row) => (
        <span class="v2-tt-dim" style={{ 'font-size': '11px' }}>
          {Object.keys(row.guilds || {}).join(', ') || '—'}
        </span>
      ),
    },
  ];

  return (
    <div class="v2-asales">
      {/* Tab + header */}
      <div class="v2-asales__tabs">
        <button
          type="button"
          class={`v2-asales__tab${tab() === 'sales'   ? ' v2-asales__tab--active' : ''}`}
          onClick={() => switchTab('sales')}
        >{EMOJI.sales} Sales</button>
        <button
          type="button"
          class={`v2-asales__tab${tab() === 'members' ? ' v2-asales__tab--active' : ''}`}
          onClick={() => switchTab('members')}
        >{EMOJI.user} Members</button>
        <span class="v2-asales__period">{period()}</span>
        <button
          type="button"
          class="v2-widget-hdr__btn"
          style={{ 'margin-left': 'auto' }}
          title="Refresh"
          onClick={() => refetch()}
        >↻</button>
      </div>

      {/* Filter bar */}
      <div class="v2-asales__filters">
        <div class="v2-asales__fg">
          <label class="v2-asales__lbl">Week</label>
          <select
            class="v2-asales__sel"
            value={weeksAgo()}
            onChange={e => {
              setWeeksAgo(Number(e.target.value));
              setStartDate('');
              setEndDate('');
            }}
          >
            <For each={WEEK_OPTS}>{o => <option value={o.value}>{o.label}</option>}</For>
          </select>
        </div>

        <div class="v2-asales__fg">
          <label class="v2-asales__lbl">Start</label>
          <input
            class="v2-asales__inp"
            type="date"
            value={startDate()}
            onInput={e => { setStartDate(e.target.value); if (e.target.value) setWeeksAgo(-1); }}
          />
        </div>

        <div class="v2-asales__fg">
          <label class="v2-asales__lbl">End</label>
          <input
            class="v2-asales__inp"
            type="date"
            value={endDate()}
            onInput={e => { setEndDate(e.target.value); if (e.target.value) setWeeksAgo(-1); }}
          />
        </div>

        <div class="v2-asales__fg">
          <label class="v2-asales__lbl">Guild</label>
          <select
            class="v2-asales__sel"
            value={guild()}
            onChange={e => setGuild(e.target.value)}
          >
            <For each={GUILDS}>{o => <option value={o.value}>{o.label}</option>}</For>
          </select>
        </div>

        <div class="v2-asales__fg">
          <label class="v2-asales__lbl">Member</label>
          <input
            class="v2-asales__inp"
            type="text"
            placeholder="@tag…"
            value={member()}
            onInput={e => setMember(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applyFilters()}
          />
        </div>

        <button type="button" class="v2-asales__apply" onClick={applyFilters}>Apply</button>
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          {/* ── Sales tab ── */}
          <Show when={tab() === 'sales'}>
            <ThemedTable
              columns={salesColumns}
              rows={sellers()}
              storageKey="admin_sales"
              defaultSortKey="profit"
              defaultSortDir="desc"
              searchPlaceholder="Search seller or guild…"
              emptyText="No sales match this filter."
              extraControls={
                <div class="v2-tt-meta-badge" style={{ color: 'var(--gold, #ffc857)' }}>
                  Total: <strong>{gold(data()?.totalProfit || 0)}</strong>
                </div>
              }
            />
          </Show>

          {/* ── Members tab ── */}
          <Show when={tab() === 'members'}>
            <ThemedTable
              columns={membersColumns}
              rows={members()}
              storageKey="admin_members"
              defaultSortKey="totalProfit"
              defaultSortDir="desc"
              searchPlaceholder="Search member…"
              emptyText="No members match this filter."
            />
          </Show>
        </Show>
      </Show>
    </div>
  );
}
